// QR con el logo del negocio en el centro y en el color de su menú (src/components/panel/qr.js).
// Acá están las cuentas puras, para poder probarlas sin navegador.

// Opciones que elige el dueño en el panel (campos qrLogo y qrColor de users/{email}).
export const QR_COLORS = {
    negro: "Negro",
    marca: "Color de tu menú"
};
export const QR_BLACK = "#000000";

// Si el plan del negocio deja usar el logo y el color en el QR.
export const qrStyleAvailable = (business, proOnly) => !proOnly || business?.subscribed === true;

// Estilo guardado, con los valores de siempre si no eligió nada: logo sí (si tiene), color negro.
export const qrStyleOf = (business, proOnly) => {
    const allowed = qrStyleAvailable(business, proOnly);
    return {
        logo: allowed && !!business?.logo && business?.qrLogo !== false,
        color: allowed && business?.qrColor === "marca" ? "marca" : "negro"
    };
}

const hexToRgb = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const rgbToHex = (rgb) => "#" + rgb.map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");

// Luminancia relativa (WCAG).
const luminance = ([r, g, b]) => {
    const lin = (v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export const contrastWithWhite = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 21;
    return 1.05 / (luminance(rgb) + 0.05);
}

// Los lectores de QR necesitan módulos oscuros sobre fondo claro. Si el color del menú es
// muy claro (amarillo, celeste), se oscurece hasta tener buen contraste con el blanco.
export const MIN_QR_CONTRAST = 4.5;
export const qrInk = (hex) => {
    let rgb = hexToRgb(hex);
    if (!rgb) return QR_BLACK;
    for (let i = 0; i < 40 && contrastWithWhite(rgbToHex(rgb)) < MIN_QR_CONTRAST; i++) {
        rgb = rgb.map((v) => v * 0.9);
    }
    return rgbToHex(rgb);
}

// Color de los módulos según lo elegido: negro o el de los botones del menú.
export const qrColorOf = (business, style) => style.color === "marca" ? qrInk(business?.buttonColor || "#2c0032") : QR_BLACK;

// Tamaño del círculo blanco del centro, como parte del ancho del QR. Con corrección de
// errores H el QR se sigue leyendo aunque se tape hasta el 30%; el círculo tapa menos del 5%.
export const LOGO_CIRCLE = 0.25;
// El logo va un poco más chico que el círculo, para que quede un borde blanco.
export const LOGO_INNER = 0.86;

// Cómo entra el logo en el círculo, mirando una versión chica del logo (RGBA de size x size):
// - "cover": las esquinas están vacías (transparentes o del mismo color que el fondo del
//   logo), así que se recorta en círculo y el logo llena el centro. Es el caso de los logos
//   redondos o con fondo liso y el dibujo en el medio.
// - "contain": hay algo en las esquinas (por ejemplo texto), así que se achica el logo
//   entero hasta que entra en el círculo, sin recortar nada.
export const logoFit = (pixels, size, width = size, height = size) => {
    if (!pixels || !size) return "contain";
    // Logos bien rectangulares no se recortan nunca.
    const ratio = width / height;
    if (ratio > 1.1 || ratio < 0.9) return "contain";

    const c = (size - 1) / 2;
    // Se mira lo que queda afuera del recorte (con un margen por el borde suavizado).
    const r = size / 2 * 0.95;
    const outside = [];
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            if ((x - c) ** 2 + (y - c) ** 2 <= r * r) continue;
            const i = (y * size + x) * 4;
            outside.push([pixels[i], pixels[i + 1], pixels[i + 2], pixels[i + 3]]);
        }
    }
    if (!outside.length) return "contain";

    // Todo transparente: logo redondo o recortado.
    if (outside.every((p) => p[3] < 24)) return "cover";
    // Todo opaco y de un solo color: fondo liso.
    if (outside.some((p) => p[3] < 230)) return "contain";
    const avg = [0, 1, 2].map((k) => outside.reduce((s, p) => s + p[k], 0) / outside.length);
    const plain = outside.every((p) => Math.abs(p[0] - avg[0]) + Math.abs(p[1] - avg[1]) + Math.abs(p[2] - avg[2]) < 48);
    return plain ? "cover" : "contain";
}

// Rectángulo donde se dibuja el logo (centrado en 0,0) para un círculo de diámetro d.
export const logoBox = (fit, width, height, d) => {
    if (!width || !height) return { w: 0, h: 0 };
    if (fit === "cover") {
        const scale = d / Math.min(width, height);
        return { w: width * scale, h: height * scale };
    }
    // Entra entero en el círculo: la diagonal del logo mide lo mismo que el diámetro.
    const scale = d / Math.hypot(width, height);
    return { w: width * scale, h: height * scale };
}
